import { escape } from 'html-escaper'
import { z } from 'zod'
import { stripHTML } from '../utils'
import { highlightCountsSchema } from './highlight-counts'

const savedSectionSchema = z
  .object({
    section: z.string(),
    text: z.string().transform((text) => stripHTML(text, true, true)),
    index1: z.number(),
    index2: z.number(),
  })
  .describe('Saved Section Schema')

export type SavedSection = z.infer<typeof savedSectionSchema>

const citationSchema = z
  .array(
    z.object({
      cited_phase: z.string(),
      document_number: z.string(),
    })
  )
  .default([])

export const basePriorArtSchema = z
  .object({
    abstract: z.string().default('').transform(escape),
    applicants: z.array(z.string()).default([]),
    application_date: z
      .string()
      .nullable()
      .default('')
      .transform((value) => value ?? ''),
    application_number: z.string().default(''),
    backward_citations: citationSchema,
    claims: z
      .array(
        z.object({
          claim_id: z.string(),
          is_dependent: z.boolean(),
          plain_text: z.string().transform(escape),
          sequence: z.number(),
        })
      )
      .default([]),
    cpc_subclass: z.string().optional(),
    cpc_symbols: z.array(z.string()).default([]),
    current_assignees: z.array(z.string()).default([]),
    current_owners: z.array(z.string()).default([]),
    is_npl: z.boolean().default(false),
    document_number: z.string(),
    family_id: z.string().nullable().default(null),
    forward_citations: citationSchema,
    google_document_number: z.string().default(''),
    inventors: z.array(z.string()).default([]),
    is_licensed: z.boolean().nullable().default(null),
    is_litigated: z.boolean().nullable().default(null),
    jurisdiction: z.string().default(''),
    kind: z.string().default(''),
    legal_status: z
      .string()
      .nullable()
      .default('')
      .transform((legalStatus) => legalStatus ?? ''),
    priority_date: z
      .string()
      .nullable()
      .default('')
      .transform((value) => value ?? ''),
    projected_expiration_date: z.union([z.string(), z.null()]).default(null),
    publication_date: z
      .string()
      .nullable()
      .default('')
      .transform((value) => value ?? ''),
    title: z.string().default(''),
    image_paths: z.array(z.string()).default([]),
    saved_sections: z.array(savedSectionSchema).default([]),
    highlight_counts: highlightCountsSchema.nullable().default(null),
    x: z.number().nullable().default(null),
    use_for_refinement: z.boolean().optional(),
    id: z.string().optional(),
    notes: z.string().default(''),
    is_child: z.boolean().default(false),
    parent_document_number: z.string().default(''),
    family_members: z
      .array(
        z.object({
          abstract: z.string().default('').transform(escape),
          applicants: z.array(z.string()).default([]),
          application_date: z
            .string()
            .nullable()
            .default('')
            .transform((value) => value ?? ''),
          application_number: z.string().default(''),
          backward_citations: citationSchema,
          claims: z
            .array(
              z.object({
                claim_id: z.string(),
                is_dependent: z.boolean(),
                plain_text: z.string().transform(escape),
                sequence: z.number(),
              })
            )
            .default([]),
          cpc_subclass: z.string().optional(),
          cpc_symbols: z.array(z.string()).default([]),
          current_assignees: z.array(z.string()).default([]),
          current_owners: z.array(z.string()).default([]),
          is_npl: z.boolean().default(false),
          document_number: z.string(),
          family_id: z.string().nullable().default(null),
          forward_citations: citationSchema,
          google_document_number: z.string().default(''),
          inventors: z.array(z.string()).default([]),
          is_licensed: z.boolean().nullable().default(null),
          is_litigated: z.boolean().nullable().default(null),
          jurisdiction: z.string().default(''),
          kind: z.string().default(''),
          legal_status: z
            .string()
            .nullable()
            .default('')
            .transform((legalStatus) => legalStatus ?? ''),
          priority_date: z
            .string()
            .nullable()
            .default('')
            .transform((value) => value ?? ''),
          projected_expiration_date: z
            .union([z.string(), z.null()])
            .default(null),
          publication_date: z
            .string()
            .nullable()
            .default('')
            .transform((value) => value ?? ''),
          title: z.string().default(''),
          image_paths: z.array(z.string()).default([]),
          saved_sections: z.array(savedSectionSchema).default([]),
          highlight_counts: highlightCountsSchema.nullable().default(null),
          x: z.number().nullable().default(null),
          use_for_refinement: z.boolean().optional(),
          id: z.string().optional(),
          notes: z.string().default(''),
          is_child: z.boolean().default(true),
          parent_document_number: z.string().default(''),
        })
      )
      .default([]),
  })
  .describe('Base Prior Art Schema')

export const apiPriorArtSchema = basePriorArtSchema

export type BasePriorArt = z.infer<typeof apiPriorArtSchema>
export type FamilyMember = Omit<BasePriorArt, 'family_members'> & {
  document_number: string
}

export const camelCaseCitationSchema = citationSchema.transform((citations) =>
  citations.map((citation) => ({
    citedPhase: citation.cited_phase,
    documentNumber: citation.document_number,
  }))
)

export type PriorArtCitations = z.infer<typeof camelCaseCitationSchema>

const transformToCamelCase = (
  priorArt: Omit<BasePriorArt, 'family_members'>,
  isChild = false,
  parentDocumentNumber = ''
) => {
  const {
    abstract,
    applicants,
    application_date,
    application_number,
    backward_citations,
    claims,
    cpc_subclass,
    cpc_symbols,
    current_assignees,
    current_owners,
    is_npl,
    document_number,
    family_id,
    forward_citations,
    google_document_number,
    inventors,
    is_licensed,
    is_litigated,
    jurisdiction,
    kind,
    legal_status,
    priority_date,
    projected_expiration_date,
    publication_date,
    title,
    image_paths,
    saved_sections,
    highlight_counts,
    x,
    use_for_refinement,
    id,
    notes,
    is_child,
    parent_document_number,
    ...rest
  } = priorArt

  return {
    abstract,
    applicants,
    applicationDate: application_date,
    applicationNumber: application_number,
    backwardCitations: backward_citations.map((citation) => ({
      citedPhase: citation.cited_phase,
      documentNumber: citation.document_number,
    })),
    claims: claims.map((claim) => ({
      claimId: claim.claim_id,
      isDependent: claim.is_dependent,
      plainText: claim.plain_text,
      sequence: claim.sequence,
    })),
    cpcSubclass: cpc_subclass,
    cpcSymbols: cpc_symbols,
    currentAssignees: [...new Set([...current_assignees, ...current_owners])],
    currentOwners: current_owners,
    isCustom: is_npl,
    documentNumber: document_number,
    familyId: family_id,
    forwardCitations: forward_citations.map((citation) => ({
      citedPhase: citation.cited_phase,
      documentNumber: citation.document_number,
    })),
    googleDocumentNumber: google_document_number,
    inventors,
    isLicensed: is_licensed,
    isLitigated: is_litigated,
    jurisdiction,
    kind,
    legalStatus: legal_status,
    priorityDate: priority_date,
    projectedExpirationDate: projected_expiration_date,
    publicationDate: publication_date,
    title,
    imagePaths: image_paths,
    savedSections: saved_sections,
    highlightCounts: highlight_counts,
    x,
    useForRefinement: use_for_refinement ?? false,
    id: id ?? '',
    notes: notes || '',
    isChild: isChild || is_child,
    parentDocumentNumber: parentDocumentNumber || parent_document_number,
    ...rest,
  }
}

const defaultFields = {
  itemType: 'result' as 'result' | 'saved' | 'hidden',
  projectId: undefined as string | undefined,
  resultNumber: undefined as
    | number
    | undefined
    | { selfResultNumber: number; parentResultNumber: number },
  patentViewed: false,
  patentViewDate: '',
}

// Transform the raw API response to a more consistent format.
export const priorArtSchema = apiPriorArtSchema.transform(
  (priorArt: BasePriorArt) => {
    const transformedPriorArt = transformToCamelCase(priorArt)

    return {
      ...transformedPriorArt,
      familyMembers: priorArt.family_members.map((familyMember) => ({
        ...transformToCamelCase(familyMember, true, priorArt.document_number),
        familyMembers: [],
        draggableId: familyMember.document_number,
        ...defaultFields,
      })),
      draggableId: [
        priorArt.document_number,
        ...priorArt.family_members.map((fm) => fm.document_number),
      ].join('-'),
      ...defaultFields,
    }
  }
)

export type PriorArt = z.infer<typeof priorArtSchema>

export type Claim = PriorArt['claims'][number]

export type ClaimTypeWithLang = Claim & {
  lang: string
}

export interface PatentDetailsType extends PriorArt {
  nextResult?: string
  prevResult?: string
  figureURLs?: string[]
  withActions?: boolean
  scroll?: boolean
}
